import {
  TellescopeModelFromName,
  transformEmailsToThreadMessages,
  useAuth,
  useEmailThreadQuery,
  useTellescopeQueries,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { Mail } from "lucide-react";
import { useMemo } from "react";
import { ReversePaginatedList } from "@tellescope/ui";
import { EmailMessage } from "../../../pages/messaging/components/conversation-panel/EmailMessage";
import { formatEmailTitle } from "@tellescope/utils";

export interface EmailContextCardProps {
  emailId: string;
}

export function EmailContextCard({ emailId }: EmailContextCardProps) {
  const { t } = useTranslation("tickets");
  const { currentUser } = useAuth();

  // Get the context email
  const emailQuery = useTellescopeQuery({
    model: "emails",
    method: "getOne",
    request: emailId,
    enabled: !!emailId,
  });

  // Get email thread using subject line matching
  const threadQuery = useEmailThreadQuery(
    {
      enduserId: emailQuery.data?.enduserId ?? "",
      subjectLine: emailQuery.data?.subject ?? "",
    },
    {
      enabled: !!emailQuery.data?.enduserId && !!emailQuery.data?.subject,
      select: (data) =>
        data.filter(
          (email) =>
            (email.timestamp ?? email.createdAt).getTime() <=
            ((emailQuery.data?.timestamp ?? emailQuery.data?.createdAt)?.getTime() ?? Infinity)
        ),
    }
  );

  // Get unique user and enduser IDs from emails
  const { userIds, enduserIds } = useMemo(() => {
    const emails = threadQuery.data ?? [];
    const userIds = new Set<string>();
    const enduserIds = new Set<string>();

    emails.forEach((email) => {
      if (email.userId) userIds.add(email.userId);
      if (email.enduserId) enduserIds.add(email.enduserId);
    });

    return {
      userIds: Array.from(userIds),
      enduserIds: Array.from(enduserIds),
    };
  }, [threadQuery.data]);

  // Query users
  const usersMap = useTellescopeQueries({
    model: "users",
    method: "getOne",
    requests: userIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"users">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: userIds.length > 0,
  });

  // Query endusers
  const endusersMap = useTellescopeQueries({
    model: "endusers",
    method: "getOne",
    requests: enduserIds,
    combine: (queries) =>
      queries.reduce<Record<string, TellescopeModelFromName<"endusers">>>((acc, curr) => {
        if (curr.data) acc[curr.data.id] = curr.data;
        return acc;
      }, {}),
    enabled: enduserIds.length > 0,
  });

  const messages = useMemo(() => {
    return transformEmailsToThreadMessages({
      list: threadQuery.data ?? [],
      endusersMap,
      usersMap,
      currentUserId: currentUser.id,
    });
  }, [currentUser.id, endusersMap, threadQuery.data, usersMap]);

  if (emailQuery.isLoading || threadQuery.isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading email thread...")}</p>
      </div>
    );
  }

  if (emailQuery.isError || threadQuery.isError || !emailQuery.data) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load email thread. Please try again.")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4 border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Mail className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Email Thread")}</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
          <span>
            {t("{{count}} email", {
              count: threadQuery.data?.length,
              defaultValue_one: "{{count}} email",
              defaultValue_other: "{{count}} emails",
            })}
          </span>
        </div>
      </div>

      {emailQuery.data.subject && (
        <div className="text-sm text-gray-700 font-medium px-3 py-2 bg-gray-50 rounded border border-gray-200">
          <span className="text-xs text-gray-500 mr-2">{t("Subject:")}</span>
          {formatEmailTitle(emailQuery.data.subject)}
        </div>
      )}

      <ReversePaginatedList
        className="border border-gray-200 rounded-lg max-h-[500px] overflow-y-auto"
        autoScrollToBottom
      >
        {messages.map((message) => (
          <EmailMessage key={message.id} message={message} className="px-4" />
        ))}
      </ReversePaginatedList>
    </div>
  );
}
